import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  FiArrowRight,
  FiBarChart2,
  FiMonitor,
  FiShield,
  FiStar,
  FiZap,
} from "react-icons/fi";
import "../styles/sponsored-banner.css";
import { fetchSponsoredBanners } from "../services/sponsoredBanner.service";

export const SLOT_TIERS = {
  basic: {
    label: "Basic",
    badgeClass: "sponsored-banner__tier--basic",
    icon: <FiShield size={12} />,
  },
  premium: {
    label: "Premium",
    badgeClass: "sponsored-banner__tier--premium",
    icon: <FiStar size={12} />,
  },
  featured: {
    label: "Featured",
    badgeClass: "sponsored-banner__tier--featured",
    icon: <FiZap size={12} />,
  },
};

function isExternalUrl(value) {
  return typeof value === "string" && /^https?:\/\//i.test(value);
}

function normalizeDestination(advertiser) {
  return (
    advertiser?.adUrl ||
    advertiser?.url ||
    advertiser?.ctaTo ||
    advertiser?.linkTo ||
    advertiser?.targetUrl ||
    "/"
  );
}

function trackAdEvent(eventType, advertiser) {
  try {
    const payload = {
      event: eventType,
      advertiserId: advertiser?.id,
      advertiserName: advertiser?.title,
      slotTier: advertiser?.slotTier || "basic",
      timestamp: new Date().toISOString(),
      page: typeof window !== "undefined" ? window.location.pathname : "",
    };

    if (typeof window !== "undefined" && Array.isArray(window.dataLayer)) {
      window.dataLayer.push({
        event: `ad_${eventType}`,
        ...payload,
      });
    }

    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent("sponsoredBannerEvent", { detail: payload })
      );
    }

    if (process.env.NODE_ENV !== "production") {
      console.info("[SponsoredBanner]", payload);
    }
  } catch (error) {
    if (process.env.NODE_ENV !== "production") {
      console.warn("[SponsoredBanner] tracking failed", error);
    }
  }
}

function BannerPlaceholder({ ctaTo = "/contact", ctaText = "Pasang Iklan" }) {
  return (
    <div className="sb-placeholder" role="region" aria-label="Slot iklan tersedia">
      <span className="sb-placeholder__icon" aria-hidden="true">
        <FiMonitor size={18} />
      </span>

      <div className="sb-placeholder__copy">
        <strong>Slot iklan tersedia</strong>
        <p>
          Tampilkan brand Anda di homepage dengan format banner premium yang
          jelas dan profesional.
        </p>
      </div>

      <div className="sb-placeholder__actions">
        <Link to={ctaTo} className="sb-placeholder__btn sb-placeholder__btn--primary">
          {ctaText}
          <FiArrowRight size={14} aria-hidden="true" />
        </Link>
        <Link to="/our-service" className="sb-placeholder__btn sb-placeholder__btn--ghost">
          Lihat Paket
        </Link>
      </div>
    </div>
  );
}

function BannerSlide({ advertiser, active, onSlideClick }) {
  const {
    bannerImage,
    bannerAlt = "Banner iklan",
    logoImage,
    logoAlt = "Logo advertiser",
    title = "Advertisement Banner",
    subtitle,
    badgeText = "Sponsored",
    slotTier = "basic",
    sizeNote,
    chips = [],
  } = advertiser || {};

  const tier = SLOT_TIERS[slotTier] || SLOT_TIERS.basic;
  const destination = normalizeDestination(advertiser);
  const external = isExternalUrl(destination);

  const Wrapper = external ? "a" : Link;
  const wrapperProps = external
    ? {
        href: destination,
        target: "_blank",
        rel: "noreferrer noopener",
      }
    : {
        to: destination,
      };

  return (
    <Wrapper
      {...wrapperProps}
      className={`sponsored-banner__slide${active ? " sponsored-banner__slide--active" : ""}`}
      aria-hidden={!active}
      onClick={() => onSlideClick?.(advertiser)}
    >
      <div className="sponsored-banner__media">
        <img
          src={bannerImage}
          alt={bannerAlt || title}
          className="sponsored-banner__image"
          loading="lazy"
          decoding="async"
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
        />
        <div className="sponsored-banner__overlay" />
      </div>

      <div className="sponsored-banner__content">
        <div className="sponsored-banner__topRow">
          <div className="sponsored-banner__brandRow">
            {logoImage && (
              <img
                src={logoImage}
                alt={logoAlt}
                className="sponsored-banner__logo"
                loading="lazy"
                decoding="async"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            )}

            <div className="sponsored-banner__brandCopy">
              <h3 className="sponsored-banner__title">{title}</h3>
              {subtitle && (
                <p className="sponsored-banner__subtitle">{subtitle}</p>
              )}
            </div>
          </div>

          <div className="sponsored-banner__badges">
            <span className="sponsored-banner__badge">
              <FiShield size={12} aria-hidden="true" />
              {badgeText}
            </span>

            <span className={`sponsored-banner__tier ${tier.badgeClass}`}>
              {tier.icon}
              {tier.label}
            </span>
          </div>
        </div>

        <div className="sponsored-banner__bottomRow">
          <div className="sponsored-banner__metaRow">
            {sizeNote && (
              <span className="sponsored-banner__metaTag">
                <FiBarChart2 size={12} aria-hidden="true" />
                {sizeNote}
              </span>
            )}
          </div>

          {chips.length > 0 && (
            <ul className="sponsored-banner__chips" aria-label="Keunggulan iklan">
              {chips.map((chip, index) => (
                <li
                  key={`${chip?.label || "chip"}-${index}`}
                  className="sponsored-banner__chip"
                >
                  {chip?.label}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Wrapper>
  );
}

function SponsoredBannerSkeleton() {
  return (
    <div className="sponsored-banner__skeleton" aria-hidden="true">
      <div className="sponsored-banner__skeletonMedia" />
      <div className="sponsored-banner__skeletonOverlay" />
      <div className="sponsored-banner__skeletonCard">
        <div className="sponsored-banner__skeletonLine sponsored-banner__skeletonLine--sm" />
        <div className="sponsored-banner__skeletonLine sponsored-banner__skeletonLine--lg" />
        <div className="sponsored-banner__skeletonLine sponsored-banner__skeletonLine--md" />
      </div>
    </div>
  );
}

const SponsoredBannerSection = ({
  advertisers: advertisersProp,
  autoPlayInterval = 5000,
  placeholderCtaTo = "/contact",
  placeholderCtaText = "Pasang Iklan",
  className = "",
}) => {
  const [advertisers, setAdvertisers] = useState(
    Array.isArray(advertisersProp) ? advertisersProp : []
  );
  const [loading, setLoading] = useState(!Array.isArray(advertisersProp));
  const [error, setError] = useState(null);
  const [current, setCurrent] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (Array.isArray(advertisersProp)) {
      setAdvertisers(advertisersProp);
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    let mounted = true;

    async function loadData() {
      try {
        setLoading(true);
        const data = await fetchSponsoredBanners({ signal: controller.signal });
        if (mounted) setAdvertisers(data);
      } catch (err) {
        if (mounted && err?.name !== "AbortError") {
          setError(err instanceof Error ? err : new Error("Failed to load banners"));
        }
      } finally {
        if (mounted) setLoading(false);
      }
    }

    loadData();

    return () => {
      mounted = false;
      controller.abort();
    };
  }, [advertisersProp]);

  const slides = useMemo(() => {
    return advertisers.filter((item) => item?.bannerImage && item?.title);
  }, [advertisers]);

  const hasAds = slides.length > 0;
  const isMultiple = slides.length > 1;

  useEffect(() => {
    setCurrent(0);
  }, [slides.length]);

  useEffect(() => {
    if (!hasAds || !slides[current]) return;
    trackAdEvent("impression", slides[current]);
  }, [current, slides, hasAds]);

  useEffect(() => {
    if (!hasAds || !isMultiple || isPaused) return;

    clearInterval(timerRef.current);
    timerRef.current = window.setInterval(() => {
      setCurrent((prev) => (prev + 1) % slides.length);
    }, autoPlayInterval);

    return () => clearInterval(timerRef.current);
  }, [hasAds, isMultiple, isPaused, autoPlayInterval, slides.length]);

  const goTo = useCallback(
    (index) => {
      setCurrent(index);
      trackAdEvent("manual_navigation", slides[index]);
    },
    [slides]
  );

  if (loading) {
    return (
      <section className={`sponsored-banner ${className}`.trim()}>
        <div className="sponsored-banner__inner">
          <SponsoredBannerSkeleton />
        </div>
      </section>
    );
  }

  return (
    <section
      className={`sponsored-banner ${className}`.trim()}
      aria-label="Sponsored banner section"
    >
      <div className="sponsored-banner__inner">
        <div className="sponsored-banner__header">
          <div className="sponsored-banner__heading">
            <p className="sponsored-banner__eyebrow">Sponsored Banner</p>
            <h2 className="sponsored-banner__sectionTitle">
              Promosikan Brand Anda
            </h2>
          </div>
        </div>

        {error ? (
          <BannerPlaceholder ctaTo={placeholderCtaTo} ctaText={placeholderCtaText} />
        ) : hasAds ? (
          <div
            className="sponsored-banner__card"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
            onFocusCapture={() => setIsPaused(true)}
            onBlurCapture={() => setIsPaused(false)}
          >
            <div
              className="sponsored-banner__track"
              style={{ transform: `translate3d(-${current * 100}%, 0, 0)` }}
            >
              {slides.map((advertiser, index) => (
                <div className="sponsored-banner__slideWrap" key={advertiser.id}>
                  <BannerSlide
                    advertiser={advertiser}
                    active={index === current}
                    onSlideClick={() => trackAdEvent("slide_click", advertiser)}
                  />
                </div>
              ))}
            </div>

            {isMultiple && (
              <>
                <div
                  className="sponsored-banner__dots"
                  role="tablist"
                  aria-label="Navigasi iklan"
                >
                  {slides.map((advertiser, index) => (
                    <button
                      key={advertiser.id}
                      type="button"
                      role="tab"
                      aria-selected={index === current}
                      aria-label={`Iklan ${index + 1}: ${advertiser.title}`}
                      className={`sponsored-banner__dot${
                        index === current ? " sponsored-banner__dot--active" : ""
                      }`}
                      onClick={() => goTo(index)}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        ) : (
          <BannerPlaceholder ctaTo={placeholderCtaTo} ctaText={placeholderCtaText} />
        )}
      </div>
    </section>
  );
};

export default SponsoredBannerSection;